import {
  getTelematicsByFilter,
  listAllTelematics,
} from '@/services/api/telematics/telematics'

/**
 * Normalizes API response to frontend format.
 * Handles new format: { trail, matchCandidates }
 * and legacy format: array of report objects.
 */
function isIgnitionOn(value) {
  if (value === true || value === 1) return true
  if (typeof value === 'string') {
    const v = value.toLowerCase()
    return v === '1' || v === 'true' || v === 'on'
  }
  return false
}

function applyVehicleFilter(allTelematics, filter) {
  const { vehicleType, vehicleClass, assignedDepot, status } = filter || {}
  let filtered = allTelematics
  if (vehicleType) {
    filtered = filtered.filter((item) => item.vehicle && item.vehicle.vehicleType === vehicleType)
  }
  if (vehicleClass) {
    filtered = filtered.filter((item) => item.vehicle && item.vehicle.vehicleClass === vehicleClass)
  }
  if (assignedDepot) {
    filtered = filtered.filter(
      (item) => item.vehicle && item.vehicle.depot && item.vehicle.depot.id === assignedDepot,
    )
  }
  if (status) {
    const wantOn = status === 'On'
    filtered = filtered.filter((item) => isIgnitionOn(item.ignition) === wantOn)
  }
  return filtered
}

function normalizeTelematicsResponse(data) {
  if (!data) return { trail: [], matchCandidates: [] }

  if (Array.isArray(data.trail)) {
    const matchCandidates = Array.isArray(data.matchCandidates) ? data.matchCandidates : []
    const trail = data.trail.map((item) => {
      const lat = item.lat ?? item.latitude
      const lng = item.lng ?? item.longitude
      const latlng =
        item.latlng ||
        (lat != null && lng != null ? `${lat},${lng}` : null)
      const ts = item.ts ?? item.timestamp
      const localTime =
        item.localTime ??
        (typeof ts === 'string' ? ts : ts != null ? new Date(ts * (ts < 1e12 ? 1000 : 1)).toISOString() : null)
      const speed = Number(item.speed ?? item.speedMph ?? 0) || 0
      const event = item.event ?? (item.reds ? 'speeding' : item.ambers ? 'idle' : 'moving')
      const timestamp = ts != null ? (ts < 1e12 ? ts * 1000 : ts) : (item.localTime ? new Date(item.localTime).getTime() : null)
      const ignition = item.ignition ?? (speed > 0 || event === 'on' || event === 'moving')
      return {
        latlng,
        localTime,
        speed,
        event,
        angle: item.angle ?? 0,
        imei: item.imei,
        ambers: item.ambers,
        reds: item.reds,
        ignition,
        timestamp,
        routeMatch: item.routeMatched ?? item.routeMatch ?? null,
        matchedStops: item.matchedStops ?? null,
        totalStops: item.totalStops ?? null,
        confidence: item.confidence ?? null,
        address: item.address ?? item.formattedAddress ?? item.place_name ?? null,
        city: item.city ?? null,
        state: item.state ?? null,
        ...item,
      }
    })
    return { trail, matchCandidates }
  }

  if (Array.isArray(data)) {
    return { trail: data, matchCandidates: [] }
  }

  return { trail: [], matchCandidates: [] }
}

export default {
  namespaced: true,
  state: {
    centerLocation: [-72.805293, 40.821545],
    selectedVehicle: {},
    vehicleIdToScroll: '',
    selectedTelematicsData: [],
    allTelematics: [],
    filteredTelematics: [],
    reports: [],
    reportsByFilter: [],
    matchCandidates: [],
    loading: false,
    filter: {
      vehicleType: '',
      vehicleClass: '',
      assignedDepot: '',
      status: '',
    },
  },
  mutations: {
    SET_CENTER_LOCATION(state, payload) {
      const coordinatesArray = payload.split(',')
      state.centerLocation = coordinatesArray.reverse()
    },
    SET_SELECT_VEHICLE(state, payload) {
      state.selectedVehicle = payload
      state.selectedTelematicsData = []
      state.selectedTelematicsData.push(payload)
    },

    SET_VEHICLE_ID_TO_SCROLL(state, payload) {
      state.vehicleIdToScroll = payload
    },

    UNSET_SELECT_VEHICLE(state, payload) {
      state.selectedVehicle = payload
      state.selectedTelematicsData = []
    },
    SET_SELECTED_TELEMATICS(state, payload) {
      if (state.selectedTelematicsData.length >= 2) {
        state.selectedTelematicsData.shift()
        state.selectedTelematicsData.push(payload)
      } else {
        state.selectedTelematicsData.push(payload)
      }
    },
    SET_LOCATION(state, payload) {
      state.centerLocation = payload
    },
    SET_TELEMATICS(state, payload) {
      state.allTelematics = payload
      state.filteredTelematics = applyVehicleFilter(state.allTelematics, state.filter)
    },
    SET_REPORTS(state, payload) {
      state.reports = payload
      state.reportsByFilter = payload
    },
    SET_REPORTS_BY_FILTER(state, payload) {
      state.reportsByFilter = payload
    },
    SET_MATCH_CANDIDATES(state, payload) {
      state.matchCandidates = payload || []
    },
    UPDATE_VEHICLE_FILTER(state, payload) {
      state.filter = { ...payload }
      state.filteredTelematics = applyVehicleFilter(state.allTelematics, state.filter)
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
  },
  actions: {
    async setCenterLocation(state, payload) {
      state.commit('SET_CENTER_LOCATION', payload)
    },

    async setSelectVehicle(state, payload) {
      state.commit('SET_SELECT_VEHICLE', payload)
    },

    async setVehicleIdToScroll(state, payload) {
      state.commit('SET_VEHICLE_ID_TO_SCROLL', payload) 
    },

    async unsetSelectVehicle(state, payload) {
      state.commit('UNSET_SELECT_VEHICLE', payload)
    },

    async updateVehicleFilter(state, payload) {
      state.commit('UPDATE_VEHICLE_FILTER', payload)
    },

    async setSelectedTelematics(state, payload) {
      state.commit('SET_SELECTED_TELEMATICS', payload)
    },

    async setLocation(state, payload) {
      state.commit('SET_LOCATION', payload)
    },

    async listAllTelematics({ commit }, payload) {
      // `silent` is passed by the background pollers (telematics + schematic).
      // A poll tick the user never asked for must not raise a toast — it fires
      // every 15s and would bury the screen on any transient failure.
      const silent = !!(payload && payload.silent)
      commit('SET_LOADING', true)
      return listAllTelematics()
        .then((telematics) => {
          commit('SET_TELEMATICS', telematics.data)
        })
        .catch((error) => {
          console.error(error)
          if (silent) return
          commit(
            'notification/NEW',
            {
              type: 'error',
              message: 'Fetch Error',
              description: 'Failed to get telematics data',
            },
            { root: true },
          )
        })
        .finally(() => commit('SET_LOADING', false))
    },

    async getReports({ commit }, payload) {
      commit('SET_LOADING', true)
      return getTelematicsByFilter(payload)
        .then((reports) => {
          const data = reports.data
          const { trail, matchCandidates } = normalizeTelematicsResponse(data)
          commit('SET_REPORTS', trail)
          commit('SET_MATCH_CANDIDATES', matchCandidates)
        })
        .catch((error) => {
          console.error(error)
          commit(
            'notification/NEW',
            {
              type: 'error',
              message: 'Fetch Error',
              description: 'Failed to get reports data',
            },
            { root: true },
          )
        })
        .finally(() => commit('SET_LOADING', false))
    },
    async setReportsByFilter({ commit }, payload) {
      return commit('SET_REPORTS_BY_FILTER', payload)
    },
    async setReports({ commit }, payload) {
      return commit('SET_REPORTS', payload)
    },
  },
}
